import React from "react";
import { Box, Stack } from "@mui/material";
import type { OperandDto, WhenDto } from "../../../../../../types/resolver";
import { defaultKeyOperandForEvaluationDomain } from "../../utils/defaultKeyOperand";
import { OperandBuilder } from "../OperandBuilder";
import type { RuleComponentProps } from "./types";

type ComparatorKey = "eq" | "ne" | "gt" | "gte" | "lt" | "lte";

export interface RuleComparatorProps extends RuleComponentProps {
    ruleType: ComparatorKey;
}

export const RuleComparator: React.FC<RuleComparatorProps> = ({
    value,
    onChange,
    ruleType,
    evaluationDomain,
    declaredDerivedAttributeNames = [],
}) => {
    const comp = value?.[ruleType];
    if (!comp) return null;

    const updateSide = (side: "left" | "right", operand: OperandDto | null) => {
        const fallback =
            side === "left" ? defaultKeyOperandForEvaluationDomain(evaluationDomain) : { value: "" };
        const next = { ...comp, [side]: operand ?? fallback };
        onChange({ [ruleType]: next } as WhenDto);
    };

    const isNumericComparator = ruleType === "gt" || ruleType === "gte" || ruleType === "lt" || ruleType === "lte";

    return (
        <Stack direction="row" spacing={2} flexWrap="wrap">
            <Box sx={{ minWidth: 200, flex: 1 }}>
                <OperandBuilder
                    label="Left"
                    side="left"
                    value={comp.left}
                    onChange={(op) => updateSide("left", op)}
                    evaluationDomain={evaluationDomain}
                    declaredDerivedAttributeNames={declaredDerivedAttributeNames}
                    leftOperandTypes={isNumericComparator ? "keyAndDerivedOnly" : "all"}
                />
            </Box>
            <Box sx={{ minWidth: 200, flex: 1 }}>
                <OperandBuilder
                    label="Right"
                    side="right"
                    leftOperand={comp.left}
                    value={comp.right}
                    onChange={(op) => updateSide("right", op)}
                    evaluationDomain={evaluationDomain}
                    declaredDerivedAttributeNames={declaredDerivedAttributeNames}
                    rightNumericOnly={isNumericComparator}
                />
            </Box>
        </Stack>
    );
};
